/* Top5CardsSection.tsx */
import React, {useEffect, useState, FC} from 'react'
import {FAST_API_URL} from '../../../modules/auth/core/_requests'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faInfoCircle} from '@fortawesome/free-solid-svg-icons'

interface ProductItem {
  asin: string
  quantity_sold: number
  revenue: number
  store: string
  product?: {
    title: string
    image: string
  }
  // Optional fields from your API response:
  quantity_refunded?: number
  quantity_orders?: number
  net_revenue?: number
}

interface Top5Data {
  top5_units: ProductItem[]
  top5_revenue: ProductItem[]
}

type TabType = 'revenue' | 'units'

const dateOptions = [
  {label: '7 days', value: '7'},
  {label: '30 days', value: '30'},
  {label: '60 days', value: '60'},
  {label: '90 days', value: '90'},
  {label: 'YTD', value: 'ytd'},
]

interface Top5CardProps {
  title: string
  category: string
  defaultDateFilter: string
}

const Top5Card: FC<Top5CardProps> = ({title, category, defaultDateFilter}) => {
  const [activeTab, setActiveTab] = useState<TabType>('revenue')
  const [dateFilter, setDateFilter] = useState<string>(defaultDateFilter)
  const [data, setData] = useState<Top5Data>({top5_units: [], top5_revenue: []})
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [showInfo, setShowInfo] = useState<{[key: number]: boolean}>({})

  const fetchData = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch(
        `${FAST_API_URL}/amz_top5?category=${category}&date_filter=${dateFilter}`
      )
      if (!response.ok) {
        throw new Error('Error fetching data')
      }
      const json = await response.json()
      // Use the keys as returned by the API
      setData({
        top5_units: json.top5_units || [],
        top5_revenue: json.top5_revenue || [],
      })
    } catch (err: any) {
      setError(err.message || 'Error fetching data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [dateFilter, category])

  const handleInfoClick = (index: number) => {
    setShowInfo((prev) => ({...prev, [index]: !prev[index]}))
  }

  // Inline styles for properties not provided by Bootstrap
  const imageContainerStyle: React.CSSProperties = {
    width: '80px',
    height: '80px',
    backgroundColor: '#fff',
    borderRadius: '15px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  }

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  }

  const popupStyle: React.CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 0.25rem)',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '0.5rem',
    border: '1px solid #555',
    backgroundColor: '#333',
    color: '#fff',
    borderRadius: '4px',
    fontSize: '0.7rem',
    whiteSpace: 'nowrap',
    zIndex: 10,
  }

  const renderList = () => {
    const items: ProductItem[] = activeTab === 'revenue' ? data.top5_revenue : data.top5_units
    if (!items || items.length === 0) {
      return <div className='text-center py-3'>No data available.</div>
    }
    return (
      <>
        {items.slice(0, 5).map((item, index) => (
          <div
            key={index}
            className='d-flex align-items-center justify-content-between border-bottom py-2 position-relative'
            style={{minHeight: '100px'}} // adjust min-height as needed
          >
            <div className='d-flex align-items-center'>
              {item.product?.image && (
                <div style={imageContainerStyle} className='me-2'>
                  <img src={item.product.image} alt={item.product.title} style={imageStyle} />
                </div>
              )}
              <div className='flex-grow-1'>
                <div className='fw-bold text-truncate' style={{maxWidth: '150px'}}>
                  {item.product?.title || item.asin}
                </div>
                <div className='small d-flex align-items-center'>
                  <span>Store:&nbsp;</span>
                  <span
                    style={{
                      color:
                        item.store.toLowerCase() === 'vintage'
                          ? 'pink'
                          : item.store.toLowerCase() === 'ecamz'
                          ? 'skyblue'
                          : 'inherit',
                    }}
                  >
                    {item.store}
                  </span>
                  <span className='position-relative ms-1'>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      style={{cursor: 'pointer'}}
                      onClick={() => handleInfoClick(index)}
                    />
                    {showInfo[index] && (
                      <div style={popupStyle}>
                        <div>
                          <strong>ASIN:</strong> {item.asin}
                        </div>
                        <div>
                          <strong>Quantity Sold:</strong> {item.quantity_sold}
                        </div>
                        {item.quantity_refunded !== undefined && (
                          <div>
                            <strong>Refunds:</strong> {item.quantity_refunded}
                          </div>
                        )}
                        {item.quantity_orders !== undefined && (
                          <div>
                            <strong>Orders:</strong> {item.quantity_orders}
                          </div>
                        )}
                        {item.net_revenue !== undefined && (
                          <div>
                            <strong>Net Revenue:</strong> ${item.net_revenue.toFixed(2)}
                          </div>
                        )}
                      </div>
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className='fw-bold small'>
              {activeTab === 'revenue'
                ? `$${item.revenue.toFixed(2)}`
                : `${item.quantity_sold} units`}
            </div>
          </div>
        ))}
      </>
    )
  }

  return (
    <div className='card h-100'>
      <div className='card-header d-flex flex-column flex-md-row justify-content-between align-items-md-center  pt-3'>
        <h3 className='card-title mb-2 mb-md-0'>{title}</h3>
        <div style={{width: '100%'}} className='d-flex align-items-center pt-3 pb-4'>
          <button
            style={{fontSize: '10px'}}
            className={`btn btn-sm btn-outline ${
              activeTab === 'revenue' ? 'btn-primary' : 'btn-outline-primary'
            }`}
            onClick={() => setActiveTab('revenue')}
          >
            Revenu
          </button>
          <button
          style={{fontSize: '10px'}}
            className={`btn btn-sm ms-2 btn-outline ${
              activeTab === 'units' ? 'btn-primary' : 'btn-outline-primary'
            }`}
            onClick={() => setActiveTab('units')}
          >
            Units Sold
          </button>
          <select
            className='form-select form-select-sm ms-2'
            value={dateFilter}
            style={{width: 'auto'}}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            {dateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='card-body'>
        {loading ? (
          <div className='text-center py-3'>Loading...</div>
        ) : error ? (
          <div className='text-center py-3 text-danger'>{error}</div>
        ) : (
          renderList()
        )}
      </div>
    </div>
  )
}

const Top5CardsSection: FC = () => {
  return (
    <div className='row' style={{height:'400px'}}>
      <div className='col-md-6 mb-4'>
        <Top5Card title='Top 5 Eyewear' category='eyewear' defaultDateFilter='7' />
      </div>
      <div className='col-md-6 mb-4'>
        <Top5Card title='Top 5 Apparel' category='apparel' defaultDateFilter='7' />
      </div>
    </div>
  )
}

export default Top5CardsSection
